import React from "react";
import cs from "classnames";
import styles from "./styles.module.scss";
import { NotificationsPopoverProps } from "./types";

/**
 * Notifications popover (Figma 321:93401). Pure presentational panel that
 * anchors itself under its trigger: render it inside a `position: relative`
 * wrapper around the bell icon — it sits below, right-aligned to the anchor.
 * On a phone it spans the top bar instead: there the wrapper stays
 * unpositioned so the bar itself is the containing block.
 * Data, open state and outside-click handling belong to the container.
 */
export const NotificationsPopover: React.FC<NotificationsPopoverProps> = ({
  items,
  title = "Notifications",
  emptyTitle = "No notifications",
  emptySubtitle = "Your notifications will appear here",
  loaderRef,
  loadingMore = false,
  className,
}) => {
  return (
    <div
      className={cs(styles.popover, className)}
      role="dialog"
      aria-label={title}
      onClick={(e) => e.stopPropagation()}
    >
      <div className={styles.header}>
        <h2 className={styles.title}>{title}</h2>
      </div>

      <div className={styles.divider} />

      {items.length > 0 ? (
        <div className={styles.list}>
          {items.map((item) => (
            <div
              key={item.id}
              className={cs(styles.item, { [styles.read]: item.read })}
            >
              <p className={styles.itemText}>{item.text}</p>
              <div className={styles.itemDateTime}>
                <span>{item.date}</span>
                <span>{item.time}</span>
              </div>
            </div>
          ))}

          <div ref={loaderRef} />

          {loadingMore && <div className={styles.loadingMore}>Loading...</div>}
        </div>
      ) : (
        <div className={styles.empty}>
          <h3 className={styles.emptyTitle}>{emptyTitle}</h3>
          <p className={styles.emptySubtitle}>{emptySubtitle}</p>
        </div>
      )}
    </div>
  );
};

export default NotificationsPopover;
